import { ReactNode } from "react";

import { MenuThreadStatusCounts } from "@/lib/components/ThreadStatusCounts/MenuThreadStatusCounts";
import { PATHS } from "@/lib/config/routesConfig";
import { iconList } from "@/lib/helpers/iconList";

type MenuLabelKey =
  | "menuHome"
  | "menuAiStudio"
  | "menuKnowledgeBases"
  | "menuNotifications"
  | "menuThreads"
  | "tabOverview";

export type MenuRenderContext = {
  pathname: string;
  isOpened: boolean;
  isNotificationsVisible: boolean;
  unreadNotifications: number;
  isThreadsVisible: boolean;
};

export type MenuActionContext = {
  navigate: (path: string) => void;
  toggleNotifications: () => void;
  toggleThreads: () => void;
};

export type MenuItem = {
  key: string;
  labelKey: MenuLabelKey;
  iconName: keyof typeof iconList;
  dataTest?: string;
  id?: string;
  expandable?: boolean;
  onClick: (actions: MenuActionContext) => void;
  isSelected: (context: MenuRenderContext) => boolean;
  getExpanded?: (context: MenuRenderContext) => boolean | undefined;
  getSecondaryLabel?: (context: MenuRenderContext) => string | undefined;
  getTrailingContent?: (context: MenuRenderContext) => ReactNode;
};

export const menuItems: MenuItem[] = [
  {
    key: "home",
    labelKey: "menuHome",
    iconName: "home",
    id: "home",
    onClick: ({ navigate }) => navigate(PATHS.HOME),
    isSelected: ({ pathname }) => pathname === PATHS.HOME,
  },
  {
    key: "ai-studio",
    labelKey: "menuAiStudio",
    iconName: "settings",
    id: "ai-studio",
    dataTest: "ai-studio-menu-button",
    onClick: ({ navigate }) => navigate(PATHS.AI_STUDIO),
    isSelected: ({ pathname }) =>
      pathname.startsWith(PATHS.AI_STUDIO) ||
      pathname.startsWith(PATHS.AGENTS),
  },
  {
    key: "studio",
    labelKey: "menuKnowledgeBases",
    iconName: "sources",
    id: "studio",
    onClick: ({ navigate }) => navigate(PATHS.STUDIO),
    isSelected: ({ pathname }) => pathname.includes(PATHS.STUDIO),
  },
  {
    key: "notifications",
    labelKey: "menuNotifications",
    iconName: "notifications",
    id: "notifications",
    onClick: ({ toggleNotifications }) => toggleNotifications(),
    isSelected: ({ isNotificationsVisible }) => isNotificationsVisible,
    getSecondaryLabel: ({ isOpened, unreadNotifications }) =>
      isOpened && unreadNotifications ? String(unreadNotifications) : undefined,
  },
  {
    key: "overview",
    labelKey: "tabOverview",
    iconName: "overview",
    id: "overview",
    dataTest: "overview-button",
    onClick: ({ navigate }) => navigate(PATHS.TABLE),
    isSelected: ({ pathname }) => pathname.startsWith(PATHS.OVERVIEW),
    getTrailingContent: ({ isOpened, isThreadsVisible }) => (
      <MenuThreadStatusCounts
        isThreadsListMounted={isOpened && isThreadsVisible}
      />
    ),
  },
  {
    key: "threads",
    labelKey: "menuThreads",
    iconName: "history",
    expandable: true,
    id: "threads",
    onClick: ({ toggleThreads }) => toggleThreads(),
    isSelected: () => false,
    getExpanded: ({ isOpened, isThreadsVisible }) =>
      isOpened ? isThreadsVisible : undefined,
  },
];